import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";

function pickErrorMessage(data, fallback) {
  if (!data) return fallback;
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (data.errors && typeof data.errors === "object") {
    for (const v of Object.values(data.errors)) {
      const arr = Array.isArray(v) ? v : [v];
      const first = arr.find((x) => typeof x === "string" && x.trim());
      if (first) return first;
    }
  }
  return fallback;
}

function UpdateProfileForm({ user, onCancel, onSuccess }) {
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState(null);

  const initialValues = {
    name: user?.name || "",
    phone_number: user?.phone_number || user?.phone || "",
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, t("authModal.register.errors.nameMin"))
      .required(t("authModal.register.errors.nameRequired")),
    phone_number: Yup.string()
      .trim()
      .min(6, t("authModal.register.errors.phoneMin"))
      .required(t("authModal.register.errors.phoneRequired")),
  });

  useEffect(() => {
    if (!message || message.status) return;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      name: values.name.trim(),
      phone_number: values.phone_number.trim(),
    };
    const locale = getSecondApiLocale(i18n.language);
    setMessage(null);
    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/updateUserInfo`,
        payload,
      );
      const ok =
        data?.isSuccess === true ||
        data?.success === true ||
        (typeof data?.message === "string" &&
          /success|updated/i.test(data.message)) ||
        !data?.errors;

      if (ok) {
        setMessage({
          status: true,
          text:
            (data && typeof data.message === "string" && data.message.trim()) ||
            t("profile.updateInfo.success"),
        });
        if (typeof onSuccess === "function") onSuccess(payload);
      } else {
        setMessage({
          status: false,
          text: pickErrorMessage(data, t("profile.updateInfo.errors.generic")),
        });
      }
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("profile.errors.network")
          : t("profile.updateInfo.errors.generic");
      setMessage({
        status: false,
        text: pickErrorMessage(error.response?.data, fallback),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="row gy-4" autoComplete="off">
          <div className="col-12">
            <h5 className="mb-1 font-20 font-heading fw-600">
              {t("profile.tabs.personal")}
            </h5>
            <p className="text-body font-14 mb-0">
              {t("profile.updateInfo.intro")}
            </p>
          </div>

          {message && (
            <div
              className={`col-12 font-14 ${
                message.status ? "text-success" : "text-danger"
              }`}
              role="status"
            >
              {message.text}
            </div>
          )}

          <div className="col-sm-6 col-xs-6">
            <label
              htmlFor="profileName"
              className="form-label mb-2 font-18 font-heading fw-600"
            >
              {t("profile.fields.name")}
            </label>
            <Field
              id="profileName"
              type="text"
              name="name"
              autoComplete="name"
              className="common-input border"
              placeholder={t("authModal.register.placeholders.name")}
            />
            <ErrorMessage
              name="name"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-sm-6 col-xs-6">
            <label
              htmlFor="profilePhone"
              className="form-label mb-2 font-18 font-heading fw-600"
            >
              {t("profile.fields.phone")}
            </label>
            <Field
              id="profilePhone"
              type="tel"
              name="phone_number"
              autoComplete="tel"
              className="common-input border"
              placeholder={t("authModal.register.placeholders.phone")}
            />
            <ErrorMessage
              name="phone_number"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-sm-12 text-end d-flex flex-wrap gap-2 justify-content-end">
            {typeof onCancel === "function" && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="btn btn-outline-main btn-lg pill mt-4 fw-500"
              >
                {t("profile.updateInfo.cancel")}
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-main btn-lg pill mt-4 fw-500"
            >
              {isSubmitting
                ? t("profile.updateInfo.btnLoading")
                : t("profile.updateInfo.btn")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default UpdateProfileForm;
